import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, FileXls, FilePdf, PencilSimple, Check, X } from "@phosphor-icons/react";
import { withDatePrefix } from "@/lib/downloadFilename";

/**
 * v1.25.28 — Master UPI Report drill-in.
 * Route: /admin/upi-mappings/:id/report
 * Shows every verified payment received on any linked UPI id of this master.
 * Columns: Date · Captured UPI · Party · UPI ID · Amount · UTR (editable amount/date via inline pencil).
 * Export: XLSX + PDF using existing verified-uploads export with the mapping's linked ids as filter.
 */
export default function UpiMappingReport() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [mapping, setMapping] = useState(null);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null);
    const [editForm, setEditForm] = useState({ amount: "", payment_date: "", utr: "" });

    const load = async () => {
        setLoading(true);
        try {
            const [{ data: mapsResp }, { data: report }] = await Promise.all([
                api.get("/upi-mappings"),
                api.get("/reports/payments-by-master-upi"),
            ]);
            const m = (mapsResp.mappings || []).find((x) => x.id === id);
            setMapping(m || null);
            const group = (report.mapped || []).find((g) => g.mapping_id === id);
            setRows(group ? group.payments : []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [id]);

    const startEdit = (row) => {
        setEditing(row.upload_id);
        setEditForm({ amount: String(row.amount || ""), payment_date: row.date || "", utr: row.utr || "" });
    };
    const saveEdit = async (row) => {
        try {
            await api.patch(`/uploads/${row.upload_id}`, {
                amount: editForm.amount === "" ? null : Number(editForm.amount),
                payment_date: editForm.payment_date || null,
                utr: editForm.utr || null,
            });
            toast.success("Payment updated");
            setEditing(null);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const exportUrl = (fmt) => `${process.env.REACT_APP_BACKEND_URL || ""}/api/upi-mappings/${id}/report.${fmt}`;
    const downloadExport = async (fmt) => {
        try {
            const token = localStorage.getItem("micm_token");
            const imp = localStorage.getItem("micm_impersonate_company");
            const headers = { Authorization: `Bearer ${token}` };
            if (imp) headers["X-Impersonate-Company"] = imp;
            const resp = await fetch(exportUrl(fmt), { headers });
            if (!resp.ok) throw new Error(await resp.text());
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = withDatePrefix(`master-upi-${mapping?.master_upi_id || id}.${fmt === "xlsx" ? "xlsx" : "pdf"}`);
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) { toast.error("Export failed: " + e.message); }
    };

    const totalAmount = rows.reduce((s, r) => s + Number(r.amount || 0), 0);

    return (
        <div className="space-y-5" data-testid="upi-mapping-report-page">
            <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => navigate("/admin/upi-mappings")}>
                    <ArrowLeft size={13} className="mr-1" /> Back
                </Button>
                <div className="flex-1">
                    <h1 className="font-display text-2xl font-bold text-slate-900">{mapping?.label || mapping?.master_upi_id || "…"}</h1>
                    <p className="text-xs text-slate-500 font-mono">{mapping?.master_upi_id} · linked: {(mapping?.linked_upi_ids || []).join(", ")}</p>
                </div>
                <Button data-testid="export-xlsx" onClick={() => downloadExport("xlsx")} className="bg-emerald-700 text-white hover:bg-emerald-800">
                    <FileXls size={14} className="mr-1" /> Excel
                </Button>
                <Button data-testid="export-pdf" onClick={() => downloadExport("pdf")} className="bg-rose-700 text-white hover:bg-rose-800">
                    <FilePdf size={14} className="mr-1" /> PDF
                </Button>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
                <div className="flex items-center justify-between bg-slate-50 px-4 py-2 text-xs">
                    <span className="text-slate-500">{rows.length} payments</span>
                    <span className="font-mono font-bold text-slate-900">Total: ₹{totalAmount.toLocaleString("en-IN")}</span>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                        <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                            <tr>
                                <th className="p-2 text-left">Date</th>
                                <th className="p-2 text-left">Captured UPI</th>
                                <th className="p-2 text-left">Party</th>
                                <th className="p-2 text-left">UTR</th>
                                <th className="p-2 text-right">Amount</th>
                                <th className="p-2 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {loading && <tr><td colSpan={6} className="p-6 text-center text-slate-500">Loading…</td></tr>}
                            {!loading && rows.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-500">No payments yet for this master.</td></tr>}
                            {rows.map((r) => {
                                const isEdit = editing === r.upload_id;
                                return (
                                    <tr key={r.upload_id} data-testid={`payment-row-${r.upload_id}`}>
                                        <td className="p-2 whitespace-nowrap font-mono text-xs">
                                            {isEdit
                                                ? <Input type="date" value={editForm.payment_date} onChange={(e) => setEditForm({ ...editForm, payment_date: e.target.value })} className="h-7 text-xs" />
                                                : r.date}
                                        </td>
                                        <td className="p-2 font-mono text-xs">{r.original_upi_id}</td>
                                        <td className="p-2 text-slate-800">{r.party_name}</td>
                                        <td className="p-2 font-mono text-xs">
                                            {isEdit
                                                ? <Input value={editForm.utr} onChange={(e) => setEditForm({ ...editForm, utr: e.target.value })} className="h-7 text-xs" />
                                                : r.utr}
                                        </td>
                                        <td className="p-2 text-right font-mono tabular-nums">
                                            {isEdit
                                                ? <Input type="number" value={editForm.amount} onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })} className="h-7 text-right text-xs" />
                                                : `₹${Number(r.amount).toLocaleString("en-IN")}`}
                                        </td>
                                        <td className="p-2 text-right">
                                            {isEdit ? (
                                                <div className="flex justify-end gap-1">
                                                    <Button size="sm" onClick={() => saveEdit(r)} className="h-6 bg-emerald-600 text-xs text-white"><Check size={11} /></Button>
                                                    <Button size="sm" variant="outline" onClick={() => setEditing(null)} className="h-6 text-xs"><X size={11} /></Button>
                                                </div>
                                            ) : (
                                                <Button data-testid={`edit-payment-${r.upload_id}`} size="sm" variant="outline" onClick={() => startEdit(r)} className="h-6 border-slate-300 text-xs">
                                                    <PencilSimple size={11} />
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
